import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { RotateCcw } from 'lucide-react';
import React from 'react';
import { BodyImageCameraOrbitIcon } from './BodyImageCameraOrbitIcon';

export type BodyImageRotationCardProps = {
  /** Rotazione accumulata in gradi. */
  angle: number;
  /** Disabilita controlli. */
  disabled?: boolean;
  /** Mostra reset a 0°. */
  showReset?: boolean;
  /** Titolo card. */
  title?: string;
  /** Layout compatto per pannello affiancato al selettore figure. */
  layout?: 'vertical' | 'compact';
  /** Rimuove il bordo esterno quando inserita in una card contenitore. */
  embedded?: boolean;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onReset?: () => void;
};

const CompactRotationPanel: React.FC<{
  title: string;
  angle: number;
  disabled: boolean;
  showReset: boolean;
  atMin: boolean;
  atMax: boolean;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onReset?: () => void;
}> = ({
  title,
  angle,
  disabled,
  showReset,
  atMin,
  atMax,
  onRotateLeft,
  onRotateRight,
  onReset,
}) => (
  <Stack gap={6} justify='center' h='100%'>
    <Group justify='space-between' align='center' gap='xs' wrap='nowrap'>
      <Text size='sm' fw={600} lh={1.2}>
        {title}
      </Text>
      <Group gap={4} wrap='nowrap'>
        <Badge
          size='sm'
          variant='light'
          color={angle === 0 ? 'gray' : 'blue'}
          miw={44}
          ta='center'
          radius='sm'
          px={6}
        >
          {angle}°
        </Badge>
        {showReset && onReset != null && (
          <Tooltip label='Reset a 0°' withArrow>
            <ActionIcon
              variant='subtle'
              size='sm'
              color='gray'
              disabled={disabled || angle === 0}
              onClick={onReset}
              aria-label='Reset rotazione'
            >
              <RotateCcw size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Group>

    <Group justify='center' align='center' gap={6} wrap='nowrap'>
      <Tooltip label='Ruota a sinistra' withArrow>
        <ActionIcon
          variant='light'
          size='md'
          disabled={disabled || atMin}
          onClick={onRotateLeft}
          aria-label='Ruota a sinistra'
        >
          ←
        </ActionIcon>
      </Tooltip>
      <BodyImageCameraOrbitIcon angle={angle} size={52} showLabel={false} />
      <Tooltip label='Ruota a destra' withArrow>
        <ActionIcon
          variant='light'
          size='md'
          disabled={disabled || atMax}
          onClick={onRotateRight}
          aria-label='Ruota a destra'
        >
          →
        </ActionIcon>
      </Tooltip>
    </Group>
  </Stack>
);

/**
 * Card compatta: icona orbita telecamera + tasti Ruota sx/dx.
 */
export const BodyImageRotationCard: React.FC<BodyImageRotationCardProps> = ({
  angle,
  disabled = false,
  showReset = true,
  title = 'Rotazione',
  layout = 'vertical',
  embedded = false,
  onRotateLeft,
  onRotateRight,
  onReset,
}) => {
  const atMin = angle <= -360;
  const atMax = angle >= 360;

  const content =
    layout === 'compact' ? (
      <CompactRotationPanel
        title={title}
        angle={angle}
        disabled={disabled}
        showReset={showReset}
        atMin={atMin}
        atMax={atMax}
        onRotateLeft={onRotateLeft}
        onRotateRight={onRotateRight}
        onReset={onReset}
      />
    ) : (
      <Stack gap='sm' justify='space-between' h='100%' flex={1}>
        <Group justify='space-between' align='center' gap='xs' wrap='nowrap'>
          <Text size='sm' fw={600}>
            {title}
          </Text>
          <Group gap={6} wrap='nowrap'>
            <Badge
              size='sm'
              variant='filled'
              color={angle === 0 ? 'gray' : 'blue'}
              miw={52}
              ta='center'
            >
              {angle}°
            </Badge>
            {showReset && onReset != null && (
              <Button
                variant='subtle'
                size='compact-sm'
                disabled={disabled || angle === 0}
                onClick={onReset}
              >
                Reset
              </Button>
            )}
          </Group>
        </Group>

        <Box
          flex={1}
          w='100%'
          px='xs'
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: embedded ? 220 : 120,
          }}
        >
          <BodyImageCameraOrbitIcon
            angle={angle}
            size={embedded ? 168 : 72}
            showLabel={false}
          />
        </Box>

        <SimpleGrid cols={2} spacing='xs' w='100%'>
          <Button
            variant='light'
            disabled={disabled || atMin}
            onClick={onRotateLeft}
          >
            ← Ruota
          </Button>
          <Button
            variant='light'
            disabled={disabled || atMax}
            onClick={onRotateRight}
          >
            Ruota →
          </Button>
        </SimpleGrid>
      </Stack>
    );

  if (embedded) {
    return (
      <Box h='100%' style={{ display: 'flex', flexDirection: 'column' }}>
        {content}
      </Box>
    );
  }

  return (
    <Card withBorder p='sm' radius='sm'>
      {content}
    </Card>
  );
};
